// ═══════════════════════════════════════════════════════════════════════════
// CHI ESEGUE LA COMMESSA — personale interno, subappalto, misto (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Decisione di Paolo (ott 2026):
//  • Interno: squadra di produzione interna (come sempre).
//  • Subappalto: i lavori li fanno una o più imprese esterne. La forza lavoro di produzione
//    interna si azzera, ma la commessa NON esce dalla pianificazione: resta il PRESIDIO
//    interno (PM, preposto, tecnico) che va in cantiere, ha ore e spese a carico della
//    commessa. Il presidio si inserisce nello stesso campo "squadra", così Pianificazione e
//    Simulatore continuano a calcolare il carico reale senza regole speciali.
//  • Misto: squadra interna + una o più imprese esterne (caso normalissimo).
// Le imprese esterne: nome (dall'anagrafica Subappaltatori, se c'è, o libero), lavorazioni
// affidate e uomini-giorno presunti (servono anche al modulo Sicurezza di cantiere: soglie
// D.Lgs. 81/08 su uomini-giorno e presenza di più imprese).
// ═══════════════════════════════════════════════════════════════════════════

export const TIPI_ESECUZIONE = {
  interno:    { id: "interno",    label: "Personale interno", breve: "Interno",    descrizione: "I lavori li esegue la vostra squadra." },
  subappalto: { id: "subappalto", label: "Subappalto",        breve: "Subappalto", descrizione: "I lavori li eseguono imprese esterne; voi seguite il cantiere con un presidio interno." },
  misto:      { id: "misto",      label: "Misto",             breve: "Misto",      descrizione: "Lavorano sia la vostra squadra sia una o più imprese esterne." },
};

/** Tipo di esecuzione della commessa (le commesse create prima di ottobre 2026 sono "interno"). */
export function tipoEsecuzione(project) {
  return TIPI_ESECUZIONE[project?.tipoEsecuzione] ? project.tipoEsecuzione : "interno";
}

/** Ci sono imprese esterne previste su questa commessa? */
export const conImpreseEsterne = (tipo) => tipo === "subappalto" || tipo === "misto";

/** Etichette della squadra interna secondo il tipo. */
export function etichettaSquadra(tipo) {
  if (tipo === "subappalto") return { titolo: "Presidio interno", aiuto: "Le persone vostre che seguiranno il cantiere (PM, preposto, tecnico): restano in pianificazione con il loro carico reale." };
  if (tipo === "misto") return { titolo: "Squadra interna", aiuto: "Le vostre persone in cantiere, oltre alle imprese esterne." };
  return { titolo: "Composizione squadra necessaria", aiuto: "" };
}

/** Impresa esterna "pulita" (campi sempre presenti, uomini-giorno numerici). */
export function normalizzaImpresa(i = {}) {
  const ug = parseFloat(String(i.uominiGiorno ?? "").replace(",", "."));
  return {
    id: i.id || ("imp_" + Math.random().toString(36).slice(2, 10)),
    nome: String(i.nome || "").trim(),
    subappaltatoreId: i.subappaltatoreId || null,
    lavorazioni: String(i.lavorazioni || "").trim(),
    uominiGiorno: Number.isFinite(ug) && ug > 0 ? Math.round(ug * 10) / 10 : null,
  };
}

/** Imprese da salvare: solo quelle con un nome; nessuna se la commessa è interna. */
export function impreseDaSalvare(tipo, imprese = []) {
  if (!conImpreseEsterne(tipo)) return [];
  return (imprese || []).map(normalizzaImpresa).filter(i => i.nome);
}

/** Avvisi non bloccanti prima di salvare. */
export function avvisiEsecuzione(tipo, imprese = []) {
  const a = [];
  if (conImpreseEsterne(tipo) && impreseDaSalvare(tipo, imprese).length === 0)
    a.push("Non hai indicato nessuna impresa esterna: potrai aggiungerla dopo, dalla commessa.");
  return a;
}

/** Giorni lavorativi (lun-ven) tra due date "AAAA-MM-GG", estremi compresi. null se mancano. */
export function giorniLavorativi(dataInizio, dataFine) {
  if (!dataInizio || !dataFine) return null;
  const a = new Date(dataInizio + "T00:00:00"), b = new Date(dataFine + "T00:00:00");
  if (isNaN(a) || isNaN(b) || b < a) return null;
  let n = 0;
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) { const g = d.getDay(); if (g !== 0 && g !== 6) n++; }
  return n;
}

/**
 * Uomini-giorno presunti della commessa: persone interne × giorni lavorativi + quanto
 * indicato per ogni impresa esterna. Restituisce anche quante imprese lavorano in cantiere
 * (la vostra conta se c'è almeno una persona interna).
 */
export function uominiGiornoPresunti(project) {
  const tipo = tipoEsecuzione(project);
  const giorni = giorniLavorativi(project?.dataInizio, project?.dataFine);
  const persone = Array.isArray(project?.squadra) && project.squadra.length > 0 ? project.squadra.length : (parseInt(project?.operaiNecessari) || 0);
  const interni = giorni === null ? null : persone * giorni;
  const imprese = impreseDaSalvare(tipo, project?.impreseEsterne);
  const esterni = imprese.reduce((s, i) => s + (i.uominiGiorno || 0), 0);
  const esterniMancanti = imprese.filter(i => i.uominiGiorno === null).length;
  return {
    interni, esterni, totale: (interni || 0) + esterni,
    completo: interni !== null && esterniMancanti === 0,
    nImprese: imprese.length + (persone > 0 ? 1 : 0),
    nImpreseEsterne: imprese.length,
  };
}

/** Descrizione breve per intestazioni e registri: "Subappalto · Rossi Srl, Bianchi Impianti". */
export function descrizioneEsecuzione(project) {
  const tipo = tipoEsecuzione(project);
  if (tipo === "interno") return TIPI_ESECUZIONE.interno.breve;
  const nomi = impreseDaSalvare(tipo, project?.impreseEsterne).map(i => i.nome);
  return TIPI_ESECUZIONE[tipo].breve + (nomi.length ? " · " + nomi.join(", ") : "");
}
